import { useEffect, useState } from 'react';
import type { StreamDebugOverlaySnapshot } from '@playcastdotio/PeerCore';
import {
  classifyImplementationName,
  formatStreamDebugOverlayLine,
} from '@playcastdotio/PeerCore';
import { guestStatsSnapshotService, type GuestStatsSnapshotService } from '../../features/stream-tuning/telemetry/GuestStatsSnapshotService';

type ConnectionHolder = {
  getRtcPeerConnection?: () => RTCPeerConnection | null;
};

type SessionWithConnection = {
  connection?: ConnectionHolder | null;
};

type ManagerWithSession = {
  session?: SessionWithConnection;
};

/** Resolve guest RTCPeerConnection from ClientPeerManager / GuestPeerSession internals. */
export function resolveGuestRtcPeerConnection(holder: unknown): RTCPeerConnection | null {
  const asManager = holder as ManagerWithSession | null | undefined;
  const session = asManager?.session ?? (holder as SessionWithConnection | null | undefined);
  const conn = session?.connection;
  if (!conn || typeof conn.getRtcPeerConnection !== 'function') return null;
  try {
    return conn.getRtcPeerConnection() ?? null;
  } catch {
    return null;
  }
}

/** Build overlay snapshot from an inbound-rtp video getStats report. */
export function snapshotFromInboundStatsReport(report: RTCStatsReport): StreamDebugOverlaySnapshot {
  let framesPerSecond = 0;
  let frameWidth = 0;
  let frameHeight = 0;
  let decoderImplementation: string | undefined;

  report.forEach((stat) => {
    if (stat.type !== 'inbound-rtp') return;
    const s = stat as RTCInboundRtpStreamStats & {
      kind?: string;
      decoderImplementation?: string;
      framesPerSecond?: number;
      frameWidth?: number;
      frameHeight?: number;
    };
    if (s.kind !== 'video') return;
    if (typeof s.framesPerSecond === 'number') {
      framesPerSecond = Math.max(framesPerSecond, s.framesPerSecond);
    }
    if (typeof s.frameWidth === 'number') frameWidth = Math.max(frameWidth, s.frameWidth);
    if (typeof s.frameHeight === 'number') frameHeight = Math.max(frameHeight, s.frameHeight);
    if (typeof s.decoderImplementation === 'string' && s.decoderImplementation) {
      decoderImplementation = s.decoderImplementation;
    }
  });

  return {
    codecKind: classifyImplementationName(decoderImplementation),
    framesPerSecond,
    frameWidth,
    frameHeight,
    implementation: decoderImplementation,
  };
}

export type StreamDebugOverlayProps = {
  snapshot: StreamDebugOverlaySnapshot;
};

/**
 * Presentational guest stream debug line (fixed over player).
 * Non-interactive — does not capture pointer events.
 */
export function StreamDebugOverlay({ snapshot }: StreamDebugOverlayProps) {
  return (
    <div
      className='pointer-events-none fixed left-3 top-3 z-[2000] rounded bg-black/60 px-2 py-1 font-mono text-xs text-emerald-300'
      data-testid='stream-debug-overlay'
      aria-hidden='true'
    >
      {formatStreamDebugOverlayLine(snapshot)}
    </div>
  );
}

export type GuestStreamDebugOverlayProps = {
  /** Optional injector for tests; defaults to ClientPeerManager / legacy peer. */
  getRtcPeerConnection?: () => RTCPeerConnection | null;
};

/**
 * Guest debug overlay subscribes to the session's authoritative sample feed.
 * Mount only when `?debug` / isDebugMode is active.
 */
export function GuestStreamDebugOverlay({ getRtcPeerConnection }: GuestStreamDebugOverlayProps = {}) {
  const snapshot = useGuestStreamDebugSnapshot(getRtcPeerConnection);
  return <StreamDebugOverlay snapshot={snapshot} />;
}

/** No extra native stats query: Phase 4 binds the live session feed to this source. */
export function useGuestStreamDebugSnapshot(
  getRtcPeerConnection?: () => RTCPeerConnection | null,
  source: GuestStatsSnapshotService = guestStatsSnapshotService,
): StreamDebugOverlaySnapshot {
  const [snapshot, setSnapshot] = useState<StreamDebugOverlaySnapshot>(() => source.latest());

  useEffect(() => {
    if (getRtcPeerConnection && source === guestStatsSnapshotService) {
      let cancelled = false;
      const pc = getRtcPeerConnection();
      if (pc && typeof pc.getStats === 'function') {
        void Promise.resolve(pc.getStats()).then((report) => {
          if (!cancelled) setSnapshot(snapshotFromInboundStatsReport(report));
        });
      }
      return () => { cancelled = true; };
    }
    setSnapshot(source.latest());
    return source.subscribe(setSnapshot);
  }, [getRtcPeerConnection, source]);

  return snapshot;
}
